<template>
  <div id="app" ref="app" :data-mode="$store.state.mode" :data-mounted="ready">
    <div class="view-container" ref="container" v-if="fontLoaded">
      <router-view :key="$route.fullPath" />
    </div>

    <div class="mode">
      <div
        class="mode__switch"
        @click="switchMode"
        @mouseenter="$store.commit('SET_POINTER', 'pointer')"
        @mouseleave="$store.commit('SET_POINTER', 'default')"
      >
        <div class="mode__icon mode__icon--switch"></div>
        <span>Change Mode</span>
      </div>

      <div
        class="mode__contact"
        @click="openContact"
        @mouseenter="$store.commit('SET_POINTER', 'pointer')"
        @mouseleave="$store.commit('SET_POINTER', 'default')"
      >
        <span>Contact Us</span>
        <div class="mode__icon mode__icon--contact"></div>
      </div>
    </div>

    <div class="webgl-container" data-webgl-container>
      <canvas data-webgl-canvas></canvas>
    </div>

    <ContactForm />

    <AudioBlock />

    <Cookiebar
      class="app__cookie"
    >
      <CookieContent
        slot="content"
        text=""
        url="https://www.iubenda.com/privacy-policy/13526818/cookie-policy"
        label="This site uses cookies for a better experience."
      />
      <CookieButton
        slot="button"
        label="Accept"
      />
    </Cookiebar>

    <CursorContainer />

    <transition name="fade">
      <Loader v-if="!$store.state.loaded" />
    </transition>

    <div class="vignette"></div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import gsap from 'gsap';
import FontFaceObserver from 'fontfaceobserver';
import CursorContainer from '@/components/CursorContainer.vue';
import ContactForm from '@/components/ContactForm.vue';
import AudioBlock from '@/components/AudioBlock.vue';
import Loader from '@/components/Loader.vue';
import hades from '@/scripts/hades';
import medusa from '@/scripts/medusa';
import apollo from '@/scripts/apollo';
import Cookiebar from '@/components/Cookie/index.vue';
import CookieContent from '@/components/Cookie/CookieContent/index.vue';
import CookieButton from '@/components/Cookie/CookieButton/index.vue';
import overlay from '@/scripts/webgl';
import audio from '@/scripts/audio';

export default {
  name: 'App',

  components: {
    CursorContainer,
    ContactForm,
    AudioBlock,
    Loader,
    Cookiebar,
    CookieContent,
    CookieButton,
  },

  data() {
    return {
      fontLoaded: false,
      ready: false,
      isSwitching: false,
    };
  },

  mounted() {
    setTimeout(() => { this.ready = true; }, 200);
    const fontNormal = new FontFaceObserver('GT America', {
      weight: 300,
    });
    const fontBold = new FontFaceObserver('GT America', {
      weight: 'normal',
    });
    const fontVHS = new FontFaceObserver('VHS', {
      weight: 'normal',
    });
    Promise.all([fontNormal.load(), fontBold.load(), fontVHS.load()]).then(() => {
      this.fontLoaded = true;
      this.$nextTick(() => {
        if (hades.ref === null) hades.init(this.$refs.app, this.$refs.container);
        if (medusa.ref === null) medusa.init();
        if (apollo.ref === null) apollo.init();
        if (overlay.ref === null) overlay.init();

        this.resize();

        this.enter();
      });
    });

    audio.load();

    window.addEventListener('resize', debounce(this.resize, 200));
  },

  methods: {
    resize() {
      const vh = window.innerHeight * 0.01;
      const rw = window.innerWidth / 1920;
      const rh = window.innerHeight / 1080;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('--rw', Math.max(rw, 0.6));
      document.documentElement.style.setProperty('--rh', Math.max(rh, 0.6));
      this.$root.$emit('resize');
      overlay.ref.resize();
    },
    enter() {
      this.$nextTick(() => {
        const medusaNodes = [...this.$el.querySelectorAll('[data-viewport]')];
        medusa.addNodes(medusaNodes);

        const medusaHTMLNodes = [...this.$el.querySelectorAll('[data-medusa-animation]')];
        medusa.addHTMLNodes(medusaHTMLNodes);
      });
    },
    switchMode() {
      if (this.isSwitching) return;
      this.isSwitching = true;
      const view = this.$el.querySelector('.view');
      const container = this.$el.querySelector('.webgl-container');
      const tl = gsap.timeline({ defaults: { duration: 1.0, ease: 'expo.inOut' } });
      tl.addLabel('start');
      tl.to(view, { y: -500, autoAlpha: 0 }, 'start');
      tl.to(container, { autoAlpha: 0 }, 'start');
      tl.add(() => {
        overlay.ref.switchMode();
        overlay.ref.pauseTicker();
        this.$store.commit('SWITCH_MODE');
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('webgl-loaded'));
          window.dispatchEvent(new CustomEvent('change-mode'));
          this.$nextTick(() => {
            const medusaNodes = [...this.$el.querySelectorAll('[data-viewport]')];
            medusa.switchMode(medusaNodes);
            overlay.ref.playTicker();
          });
        }, 300);
        hades.ref.scrollTo({ x: 0, y: 0 }, 0);
      });
      tl.set(view, { y: 500 });
      tl.addLabel('half');
      tl.to(view, { y: 0, autoAlpha: 1 }, 'half');
      tl.to(container, { autoAlpha: 1 }, 'half');
      tl.then(() => { this.isSwitching = false; });
    },
    openContact() {
      this.$store.commit('SET_CONTACT', true);
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/base.scss";
@import "@/styles/common.scss";

.webgl-container {
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100vw;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);

  pointer-events: none;
}

.vignette {
  @extend %full-fixed;
  pointer-events: none;
  z-index: 10;
  box-shadow: inset 0 0 10em 5em rgba(0, 0, 0, .75);
}

.mode {
  position: absolute;
  width: 100%;
  top: calc((100vh / 18) * 1);
  padding: 30px calc((100vw / 32) * 1);
  opacity: 0;

  display: flex;
  align-items: center;
  justify-content: space-between;

  overflow: hidden;

  z-index: 11;
  mix-blend-mode: difference;

  &__switch,
  &__contact {
    transform: translateY(-100%);
    transition: transform .6s $inOutCubic;
    transition-delay: .2s;
    padding: 5px 0px;

    @at-root [data-mounted] & {
      transform: translateY(0px);
    }

    @at-root [data-mode="estate"] & {
      color: $color-white;
    }
  }

  &__switch,
  &__contact {
    display: flex;
    align-items: center;
    cursor: none;

    span {
      display: inline-block;
      margin: 0px 10px;
    }
  }

  &__contact {
    @include mq($until: ipadP) {
      text-align: right;
    }
  }

  &__icon {
    position: relative;
    top: 2px;
    width: 20px;
    height: 20px;
    background-position: center center;
    background-size: contain;
    background-repeat: no-repeat;

    @at-root [data-mode="estate"] & {
      transform: rotate(-180deg);
    }

    &--switch {
      background-image: url('/static/vector/switch.svg');
    }

    &--contact {
      background-image: url('/static/vector/contact.svg');
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .3s ease-in-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
